import { CommonModule } from '@angular/common';
import { Component, HostBinding, OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, ElementRef } from '@angular/core';

import { toDataURL } from 'qrcode';
import { Subscription } from 'rxjs';

import { GenericUtils } from '@unpispas/upp-defs';
import { languageService, languageSupport } from '@unpispas/upp-base';

import { Ticket } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { TicketData, TicketDataRegistry } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
/********************************/
/* Public ticket print model    */
/********************************/

type TicketPrintItem = {
    count: number;
    name: string;
    refund: boolean;
    cancel: boolean;
    offer: boolean;
    fixed: boolean;
    isweight: boolean;
    price: number;
    charge: number;
    house: boolean;
    adjust: number;
    options: Array<{
        name: string;
        price: number;
        total: number;
    }>;
};

type TicketPrintMenuProduct = {
    size: number;
    price: number;
    charge: number;
    product: {
        name: string;
    };
};

/** Recipient shape in registry (canonical fields + optional structured address). */
type PrintRecipient = {
    name?: string | null;
    taxId?: string | null;
    country?: string | null;
    address?: {
        formatted?: string | null;
        street?: string | null;
        number?: string | null;
        zipcode?: string | null;
        town?: string | null;
        province?: string | null;
    } | null;
    street?: string | null;
    number?: string | null;
    postalcode?: string | null;
    zipcode?: string | null;
    town?: string | null;
    city?: string | null;
    municipality?: string | null;
    region?: string | null;
    province?: string | null;
};

type TicketPrintMenu = {
    count: number;
    name: string;
    price: number;
    total: number;
    fixed: boolean;
    house: boolean;
    adjust: number;
    products: TicketPrintMenuProduct[];
};

type TicketPrintExtra = {
    name: string;
    total: number;
};

type TicketPrintDiscount = {
    name: string;
    percent: number | null;
    total: number;
};

/** 80 mm thermal ticket roll or A5 sheet invoice. Unknown strings normalize to `thermal`. */
export type TicketPrintFormat = 'thermal' | 'A5';

export type TicketPrintInfo = {
    logo: string | null;
    place: string;
    table: number | null;
    phone: string | null;
    address: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    invoice: {
        business: string;
        taxid: string;
        address: string | null;
        address_line1?: string | null;
        address_line2?: string | null;
        postalcode: string | null;
        town?: string | null;
        region: string | null;
    } | null;
    business: string | null;
    taxid: string | null;
    fiscal: string | null;
    fiscal_line1?: string | null;
    fiscal_line2?: string | null;
    created_date: string | null;
    created_series: string | null;
    created_invceno: string | null;
    invoice_date: string | null;
    invoice_series: string | null;
    invoice_invceno: string | null;
    date: string;
    time: string;
    order: string;
    comments: string | null;
    draft: boolean;
    cancelled: boolean;
    products: TicketPrintItem[];
    menus: TicketPrintMenu[];
    extras: TicketPrintExtra[];
    offers: TicketPrintMenu[];
    discounts: TicketPrintDiscount[];
    total: number;
    base: number;
    taxes: number;
    rates: Array<{ rate: number; taxes: number; base: number }>;
    diners: number | null;
    showpay: boolean;
    payment: string | null;
    given: number | null;
    return: number | null;
    cash: number;
    card: number;
    ticketbai_info: string | null;
    ticketbai_qr_value: string | null;
    verifactu_qr_value: string | null;
};

/********************************/
/* Internal helper types        */
/********************************/

type PlacePrintOptions = {
    optionExtendTicket: boolean;
    optionHidePaymentMethod: boolean;
    optionPrintLogo: boolean;
    optionPlaceLogo: string | null;
};

type PlaceIdentity = {
    business: string | null;
    taxid: string | null;
    fiscal: string | null;
    fiscal1: string | null;
    fiscal2: string | null;
};

type PlaceContact = {
    phone: string | null;
    address: string | null;
    address1: string | null;
    address2: string | null;
};

type FiscalRefParts = {
    series: string | null;
    invceno: string | null;
};

type ReferenceBlock = {
    createdDate: string | null;
    createdSeries: string | null;
    createdInvceno: string | null;
    invoiceDate: string | null;
    invoiceSeries: string | null;
    invoiceInvceno: string | null;
};

type TotalsBlock = {
    total: number;
    base: number;
    taxes: number;
    rates: TicketPrintInfo['rates'];
};

type PaymentBlock = {
    showpay: boolean;
    payment: string | null;
    given: number | null;
    return: number | null;
    cash: number;
    card: number;
};

type FiscalQrBlock = {
    ticketbai_info: string | null;
    ticketbai_qr_value: string | null;
    verifactu_qr_value: string | null;
};

const PAYMENT_METHODS_CASH = ['PAYCASH', 'CASHPAY', 'CASH'];
const PAYMENT_METHODS_CARD = ['PAYCARD', 'CARDPAY', 'CARD'];
const PAYMENT_METHODS_LINE = ['PAYLINE', 'LINEPAY', 'LINE'];
const PAYMENT_METHODS_ACCOUNT = ['PAYACCOUNT', 'ACCOUNTPAY', 'ACCOUNT'];
const CANCEL_TICKET_STATUS = ['CC', 'CANCEL'];
const CANCEL_COMMERCIAL_STATE = ['CANCEL', 'DELETE'];

/********************************/
/* Data calculations            */
/********************************/

class TicketToPrint {
    private _info: TicketPrintInfo | null = null;

    constructor(
        private ticketdata: TicketData,
        private ticket: Ticket | null = null,
        private place: PlaceView | null = null,
        private data: dataService,
    ) {}

    info(refresh = false): TicketPrintInfo | null {
        if (refresh || this._info === null){
            this._info = this.BuildInfo();
        }

        return this._info;
    }

    /**
     * Normalizes Place.logo (`string` or `{ url, b64 }`) to an `<img src>` value.
     * Prefers embedded `b64` (with `data:` prefix when missing).
     */
    static ResolveLogoSrc(logo: unknown): string | null {
        if (logo == null || logo === false){
            return null;
        }

        if (typeof logo === 'string'){
            const _raw = logo.trim();
            if (_raw.length === 0){
                return null;
            }
            if (_raw.startsWith('data:image')){
                return _raw;
            }
            if (_raw.startsWith('http://') || _raw.startsWith('https://') || _raw.startsWith('/')){
                return _raw;
            }
            return `data:image/jpeg;base64,${_raw}`;
        }

        if (typeof logo === 'object'){
            const _file = logo as { url?: string | null; b64?: string | null };
            const _b64 = _file.b64 != null ? String(_file.b64).trim() : '';
            if (_b64.length > 0){
                return _b64.startsWith('data:image') ? _b64 : `data:image/jpeg;base64,${_b64}`;
            }
            const _url = _file.url != null ? String(_file.url).trim() : '';
            return _url.length > 0 ? _url : null;
        }

        return null;
    }

    /**
     * Ensures the logo is a data URL suitable for offline HTML→PDF (wkhtmltopdf).
     * Remote URLs are fetched in the browser and re-encoded; failures yield `null`.
     */
    static async EmbedLogoSrc(logo: string): Promise<string | null> {
        const _src = logo.trim();
        if (_src.length === 0){
            return null;
        }
        if (_src.startsWith('data:image')){
            return _src;
        }
        if (!_src.startsWith('http://') && !_src.startsWith('https://') && !_src.startsWith('/')){
            return `data:image/jpeg;base64,${_src}`;
        }

        try {
            return await GenericUtils.imageTobase64(_src, 'image/jpeg', 1.0, 0.8);
        }
        catch (error) {
            console.error('[TICKET PRINT] Failed to embed logo for device print', error);
            return null;
        }
    }

    /**
     * Replaces a remote logo URL with an embedded data URL so device HTML→PDF
     * (wkhtmltopdf) does not fetch assets over the network.
     */
    async EmbedLogoForDevice(): Promise<void> {
        if (!this._info?.logo){
            return;
        }

        this._info.logo = await TicketToPrint.EmbedLogoSrc(this._info.logo);
    }

    private BuildInfo(): TicketPrintInfo | null {
        const _registry = this.ticketdata.Registry;
        if (_registry == null){
            return null;
        }
        return this.BuildInfoFromRegistry(_registry);
    }

    /* === Orchestration === */

    private BuildInfoFromRegistry(registry: TicketDataRegistry): TicketPrintInfo {
        const _placeOptions = this.ResolvePlacePrintOptions();
        const _placeIdentity = this.ResolvePlaceIdentity();
        const _placeContact = this.ResolvePlaceContact();
        const _issuedDate = this.ParseDate(registry.fiscal?.issuedAt || null);

        const _customer = this.BuildCustomerBlock(registry);
        const _references = this.BuildReferenceBlock(registry, _issuedDate, _customer != null);
        const _lineIndex = this.BuildLineIndex(registry);
        const _products = this.BuildPrintableProducts(registry, _placeOptions);
        const _menus = this.BuildMenusFromCommercialRegistry(registry, _lineIndex, true, _placeOptions.optionExtendTicket);
        const _offers = this.BuildMenusFromCommercialRegistry(registry, _lineIndex, false, true);
        const _totals = this.BuildTotalsBlock(registry);
        const _payment = this.BuildPaymentBlock(registry, _placeOptions);
        const _fiscalQr = this.ResolveFiscalQrFromRegistry(registry);

        const _merchantName = String(registry.merchant?.name || '').trim();
        const _merchantTaxId = String(registry.merchant?.taxId || '').trim();

        return {
            logo: _placeOptions.optionPrintLogo ? (_placeOptions.optionPlaceLogo || null) : null,
            place: _merchantName || '-',
            table: this.ticket?.qrcode?.number ?? null,
            phone: _placeContact.phone,
            address: _placeContact.address,
            address_line1: _placeContact.address1,
            address_line2: _placeContact.address2,
            invoice: _customer,
            business: _placeIdentity.business || _merchantName || null,
            taxid: _placeIdentity.taxid || _merchantTaxId || null,
            fiscal: _placeIdentity.fiscal,
            fiscal_line1: _placeIdentity.fiscal1,
            fiscal_line2: _placeIdentity.fiscal2,
            created_date: _references.createdDate,
            created_series: _references.createdSeries,
            created_invceno: _references.createdInvceno,
            invoice_date: _references.invoiceDate,
            invoice_series: _references.invoiceSeries,
            invoice_invceno: _references.invoiceInvceno,
            date: _issuedDate?.toLocaleDateString() || '',
            time: _issuedDate?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '',
            order: this.ResolveOrderNo(registry),
            comments: null,
            draft: this.ResolveDraft(registry),
            cancelled: CANCEL_COMMERCIAL_STATE.includes(String(registry.commercial?.state || '').toUpperCase()),
            products: _products,
            menus: _menus,
            extras: this.BuildExtrasBlock(registry),
            offers: _offers,
            discounts: this.BuildDiscountsBlock(registry),
            total: _totals.total,
            base: _totals.base,
            taxes: _totals.taxes,
            rates: _totals.rates,
            diners: null,
            // Live unpaid → proforma only (no payment method / no "payment requested").
            // Paid or payload-only → registry commercial.payments.
            showpay: this.ResolveShowPay(_payment.showpay),
            payment: this.ResolvePrintPayment(_payment.payment),
            given: this.ticket == null || this.ticket.PaidFlag ? _payment.given : null,
            return: this.ticket == null || this.ticket.PaidFlag ? _payment.return : null,
            cash: this.ticket == null || this.ticket.PaidFlag ? _payment.cash : 0,
            card: this.ticket == null || this.ticket.PaidFlag ? _payment.card : 0,
            ticketbai_info: _fiscalQr.ticketbai_info,
            ticketbai_qr_value: _fiscalQr.ticketbai_qr_value,
            verifactu_qr_value: _fiscalQr.verifactu_qr_value,
        };
    }

    /* === Customer / references === */

    private BuildCustomerBlock(registry: TicketDataRegistry): TicketPrintInfo['invoice'] {
        const _customer = registry.recipient as PrintRecipient | null;
        if (_customer == null){
            return null;
        }

        const _name = String(_customer.name || '').trim();
        const _taxId = String(_customer.taxId || '').trim();
        const _address = _customer.address ?? null;
        const _addressFormatted = String(_address?.formatted || '').trim();
        const _street = String(_address?.street || _customer.street || '').trim();
        const _number = String(_address?.number || _customer.number || '').trim();
        const _postalCode = String(_customer.postalcode || _customer.zipcode || _address?.zipcode || '').trim();
        const _town = String(_customer.town || _customer.city || _customer.municipality || _address?.town || '').trim();
        const _region = String(_customer.region || _customer.province || _address?.province || '').trim();

        let _addressLine1 = [_street, _number].filter((item) => item.length > 0).join(', ');
        let _addressLine2 = [_postalCode, _town, _region].filter((item) => item.length > 0).join(' ');
        if (_addressLine1.length === 0 && _addressLine2.length === 0 && _addressFormatted.length > 0){
            const _formattedParts = _addressFormatted
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item.length > 0);
            if (_formattedParts.length > 0){
                _addressLine1 = _formattedParts.shift() || '';
                _addressLine2 = _formattedParts.join(', ');
            }
        }

        return {
            business: _name || 'Cliente',
            taxid: _taxId || '-',
            address: _addressFormatted || null,
            address_line1: _addressLine1 || null,
            address_line2: _addressLine2 || null,
            postalcode: _postalCode || null,
            town: _town || null,
            region: _region || null,
        };
    }

    private BuildReferenceBlock(registry: TicketDataRegistry, issuedDate: Date | null, hasInvoice: boolean): ReferenceBlock {
        const _liveSeries = String(registry.ticketRef?.series || '').trim();
        const _liveInvoice = String(registry.ticketRef?.invceno || '').trim();
        const _simplified = this.ResolveSimplifiedTicketFromChanges(registry);
        const _series = _simplified?.series || _liveSeries;
        const _invoice = _simplified?.invceno || _liveInvoice;
        const _formattedDate = issuedDate?.toLocaleString() || null;
        const _invoiceRef = hasInvoice
            ? this.ResolveInvoiceReference(registry, _liveSeries, _liveInvoice)
            : { series: null, invceno: null };
        return {
            createdDate: _formattedDate,
            createdSeries: _series || null,
            createdInvceno: _invoice || null,
            invoiceDate: hasInvoice ? _formattedDate : null,
            invoiceSeries: _invoiceRef.series,
            invoiceInvceno: _invoiceRef.invceno,
        };
    }

    /** Original simplified S/T from the first fiscal change, when ticketRef was renumbered to F/R. */
    private ResolveSimplifiedTicketFromChanges(registry: TicketDataRegistry): { series: string; invceno: string } | null {
        const _changes = Array.isArray(registry.changes) ? registry.changes : [];
        for (const _change of _changes){
            const _series = String(_change?.series || '').trim();
            const _invoice = String(_change?.invoice || '').trim();
            if (_series.startsWith('S') && _invoice.startsWith('T')){
                return { series: _series, invceno: _invoice };
            }
        }
        return null;
    }

    private ResolveOrderNo(registry: TicketDataRegistry): string {
        const fromRegistry = String(registry.ticketRef?.orderno || '').trim();
        if (fromRegistry !== ''){
            return fromRegistry;
        }

        return String(this.ticket?.orderno || '').trim();
    }

    /* === Lines and products === */

    private BuildLineIndex(registry: TicketDataRegistry): Map<number, any> {
        const _lines = registry.commercial?.lines || [];
        const _index = new Map<number, any>();
        for (const [position, line] of _lines.entries()){
            if (line.objid != null){
                _index.set(Number(line.objid), line);
            }
            _index.set(-(position + 1), line);
        }
        return _index;
    }

    private BuildPrintableProducts(registry: TicketDataRegistry, placeOptions: PlacePrintOptions): TicketPrintItem[] {
        const _lines = registry.commercial?.lines || [];
        const _menuOfferRefs = this.CollectMenuOfferReferences(registry);
        return _lines
            .filter((line) => this.IsPrintableLine(line, _menuOfferRefs))
            .map((line) => this.BuildPrintProduct(line, registry, placeOptions));
    }

    private CollectMenuOfferReferences(registry: TicketDataRegistry): Set<number> {
        const _offers = registry.commercial?.offers || [];
        const _refs = new Set<number>();
        for (const _entry of _offers){
            if (!_entry?.isMenu){
                continue;
            }

            const _rawRef = _entry.objid;
            if ((_rawRef === null) || (_rawRef === undefined)){
                continue;
            }

            const _ref = Number(_rawRef);
            if (!Number.isFinite(_ref)){
                continue;
            }

            _refs.add(_ref);
        }
        return _refs;
    }

    private IsPrintableLine(line: any, menuOfferRefs: Set<number>): boolean {
        const _qty = Number(line?.qty || 0);
        if ((line?.name == null) && (_qty === 0) && (line?.offer == null)){
            return false;
        }

        const _lineOfferRef = line?.offer;
        if ((_lineOfferRef === null) || (_lineOfferRef === undefined)){
            return true;
        }

        const _ref = Number(_lineOfferRef);
        if (!Number.isFinite(_ref)){
            return true;
        }

        return !menuOfferRefs.has(_ref);
    }

    private BuildPrintProduct(line: any, registry: TicketDataRegistry, placeOptions: PlacePrintOptions): TicketPrintItem {
        const _qty = Number(line.qty || 0);
        const _lineTotal = Number(line.amounts?.total || 0);
        const _linePrint = line?.print || null;
        const _shortcutName = line?.shortcut?.name?.label || '';
        const _ticketStatus = String(registry.ticketRef?.status || '').trim().toUpperCase();
        const _isRefund = _linePrint != null
            ? !!_linePrint.refund
            : _lineTotal < 0;
        const _isCancel = _linePrint != null
            ? !!_linePrint.cancel
            : CANCEL_TICKET_STATUS.includes(_ticketStatus);
        const _options = placeOptions.optionExtendTicket
            ? this.MapLineOptions(line)
            : [];
        const _amounts = this.ResolveLinePrintAmounts(_qty, _lineTotal, _linePrint, _options);
        const _lineName = line.name?.label || '';
        const _displayName = placeOptions.optionExtendTicket
            ? _lineName
            : (_shortcutName || _lineName);

        return {
            count: _qty,
            name: _displayName,
            refund: _isRefund,
            cancel: _isCancel,
            offer: line.offer != null,
            fixed: !!_linePrint?.fixed,
            isweight: !!_linePrint?.isweight,
            price: _amounts.price,
            charge: _amounts.charge,
            house: !!_linePrint?.house,
            adjust: Number(_linePrint?.adjust || 0),
            options: _options,
        };
    }

    private MapLineOptions(line: any): TicketPrintItem['options'] {
        return (line.options || []).map((option: any) => ({
            name: option.name?.label || option.group?.label || '',
            price: Number(option?.price || 0),
            total: Number(option?.total || 0),
        }));
    }

    private ResolveLinePrintAmounts(
        qty: number,
        lineTotal: number,
        linePrint: { price?: number; charge?: number } | null,
        options: Array<{ total: number }>,
    ): { price: number; charge: number } {
        const _basePrice = linePrint != null
            ? Number(linePrint.price || 0)
            : (qty > 0 ? lineTotal / qty : lineTotal);
        const _baseCharge = linePrint != null
            ? Number(linePrint.charge || 0)
            : lineTotal;

        if (options.length === 0){
            return { price: _basePrice, charge: _baseCharge };
        }

        const _optionsTotal = options.reduce((sum, option) => sum + Number(option.total || 0), 0);
        const _displayCharge = _baseCharge - _optionsTotal;
        return {
            price: qty > 0 ? (_displayCharge / qty) : _displayCharge,
            charge: _displayCharge,
        };
    }

    /* === Menus and offers === */

    private BuildMenusFromCommercialRegistry(
        registry: TicketDataRegistry,
        linesByReference: Map<number, any>,
        isMenu: boolean,
        includeProducts: boolean,
    ): TicketPrintMenu[] {
        const _offers = registry.commercial?.offers || [];
        return _offers
            .filter((entry) => !!entry.isMenu === isMenu)
            .map((entry) => this.BuildMenuOrOfferEntry(entry, linesByReference, includeProducts));
    }

    private BuildMenuOrOfferEntry(
        entry: any,
        linesByReference: Map<number, any>,
        includeProducts: boolean,
    ): TicketPrintMenu {
        const _products = includeProducts
            ? (entry.appliesTo || []).map((lineObjId: any) => this.BuildMenuProduct(linesByReference, lineObjId))
            : [];
        const _productsTotal = _products.reduce(
            (sum: number, product: TicketPrintMenuProduct) => sum + Number(product.charge || 0),
            0,
        );
        const _offerTotal = Number(entry.total);
        const _resolvedTotal = Number.isFinite(_offerTotal) ? _offerTotal : _productsTotal;
        const _rawQty = Number(entry.qty);
        const _count = (Number.isFinite(_rawQty) && (_rawQty > 0)) ? _rawQty : 1;

        return {
            count: _count,
            name: entry.name?.label || '',
            price: _count > 0 ? (_resolvedTotal / _count) : _resolvedTotal,
            total: _resolvedTotal,
            fixed: false,
            house: false,
            adjust: 0,
            products: _products,
        };
    }

    private BuildMenuProduct(linesByReference: Map<number, any>, lineObjId: any): TicketPrintMenuProduct {
        const _line = linesByReference.get(Number(lineObjId)) || null;
        const _lineTotal = Number(_line?.amounts?.total || 0);
        const _lineQty = Number(_line?.qty || 0);
        return {
            size: _lineQty,
            price: _lineQty > 0 ? (_lineTotal / _lineQty) : _lineTotal,
            charge: _lineTotal,
            product: {
                name: _line?.name?.label || '',
            },
        };
    }

    /* === Extras and discounts === */

    private BuildExtrasBlock(registry: TicketDataRegistry): TicketPrintExtra[] {
        return (registry.commercial?.extras || []).map((extra) => ({
            name: extra.name?.label || '',
            total: Number(extra.amounts?.total || 0),
        }));
    }

    private BuildDiscountsBlock(registry: TicketDataRegistry): TicketPrintDiscount[] {
        return (registry.commercial?.discounts || []).map((discount) => ({
            name: discount.name?.label || '',
            percent: null,
            total: Math.abs(Number(discount.amounts?.total || 0)),
        }));
    }

    /* === Totals and payments === */

    private BuildTotalsBlock(registry: TicketDataRegistry): TotalsBlock {
        const _totals = registry.commercial?.totals || { base: 0, amount: 0, total: 0, taxes: [] };
        const _rates = (_totals.taxes || []).map((tax) => ({
            rate: Number(tax.rate || 0),
            taxes: Number(tax.amount || 0),
            base: Number(tax.base || 0),
        }));
        return {
            total: Number(_totals.total || 0),
            base: Number(_totals.base || 0),
            taxes: Number(_totals.amount || 0),
            rates: _rates,
        };
    }

    private BuildPaymentBlock(registry: TicketDataRegistry, placeOptions: PlacePrintOptions): PaymentBlock {
        const _payments = registry.commercial?.payments || [];
        const _cashPayment = this.FindPayment(_payments, PAYMENT_METHODS_CASH);
        return {
            showpay: _payments.length > 0 && !placeOptions.optionHidePaymentMethod,
            payment: this.ResolvePaymentMethod(_payments),
            given: _cashPayment?.given != null ? Number(_cashPayment.given) : null,
            return: _cashPayment?.change != null ? Number(_cashPayment.change) : null,
            cash: this.ResolvePaymentAmount(_payments, PAYMENT_METHODS_CASH),
            card: this.ResolvePaymentAmount(_payments, PAYMENT_METHODS_CARD),
        };
    }

    /** Live unpaid → always proforma; payload-only trusts registry fiscal.status. */
    private ResolveDraft(registry: TicketDataRegistry): boolean {
        if (this.ticket != null && !this.ticket.PaidFlag){
            return true;
        }
        return registry.fiscal?.status === 'DRAFT';
    }

    /** Paid/payload → registry method; live unpaid → hide (draft/proforma only). */
    private ResolvePrintPayment(registryPayment: string | null): string | null {
        if (this.ticket == null || this.ticket.PaidFlag){
            return registryPayment;
        }
        return null;
    }

    private ResolveShowPay(registryShowPay: boolean): boolean {
        if (this.ticket == null || this.ticket.PaidFlag){
            return registryShowPay;
        }
        return false;
    }

    private ResolvePaymentMethod(payments: Array<any>): string | null {
        if (payments.length === 0){
            return null;
        }
        if (payments.length > 1){
            return 'PAYMIXED';
        }

        const _method = String(payments[0]?.method || '').trim().toUpperCase();
        if (PAYMENT_METHODS_CASH.includes(_method)){
            return 'PAYCASH';
        }
        if (PAYMENT_METHODS_CARD.includes(_method)){
            return 'PAYCARD';
        }
        if (PAYMENT_METHODS_LINE.includes(_method)){
            return 'PAYLINE';
        }
        if (PAYMENT_METHODS_ACCOUNT.includes(_method)){
            return 'PAYACCOUNT';
        }
        return null;
    }

    private ResolvePaymentAmount(payments: Array<any>, methods: string[]): number {
        return payments.reduce((amount, payment) => {
            const _method = String(payment?.method || '').trim().toUpperCase();
            if (!methods.includes(_method)){
                return amount;
            }
            return amount + Number(payment?.amount || 0);
        }, 0);
    }

    private FindPayment(payments: Array<any>, methods: string[]): any | null {
        for (const _payment of payments){
            const _method = String(_payment?.method || '').trim().toUpperCase();
            if (methods.includes(_method)){
                return _payment;
            }
        }
        return null;
    }

    /* === Place resolution === */

    private ResolvePlaceIdentity(): PlaceIdentity {
        if (!this.place){
            return {
                business: null,
                taxid: null,
                fiscal: null,
                fiscal1: null,
                fiscal2: null,
            };
        }


        return {
            business: this.place.business || null,
            taxid: this.place.taxid || null,
            fiscal: this.place.fiscal?.formatted || null,
            fiscal1: this.place.fiscal?.line1 || null,
            fiscal2: this.place.fiscal?.line2 || null,
        };
    }

    private ResolvePlacePrintOptions(): PlacePrintOptions {
        if (!this.place){
            return {
                optionExtendTicket: true,
                optionHidePaymentMethod: false,
                optionPrintLogo: false,
                optionPlaceLogo: null,
            };
        }

        return {
            optionExtendTicket: this.place.GetConfigOption('optionExtendTicket', true),
            optionHidePaymentMethod: this.place.GetConfigOption('optionHidePaymentMethod', false),
            optionPrintLogo: this.place.GetConfigOption('optionPrintLogo', false),
            optionPlaceLogo: TicketToPrint.ResolveLogoSrc(this.place.logo),
        };
    }

    private ResolvePlaceContact(): PlaceContact {
        if (!this.place){
            return {
                phone: null,
                address: null,
                address1: null,
                address2: null,
            };
        }

        return {
            phone: this.place.phone || null,
            address: this.place.address?.formatted || null,
            address1: this.place.address?.line1 || null,
            address2: this.place.address?.line2 || null,
        };
    }

    /* === Parsing helpers === */

    private ParseDate(value: string | null): Date | null {
        if (!value){
            return null;
        }

        const _date = new Date(value);
        return Number.isNaN(_date.getTime()) ? null : _date;
    }

    /* === Invoice reference resolution === */

    private ResolveInvoiceReference(registry: TicketDataRegistry, series: string, invoice: string): FiscalRefParts {
        const _fromChanges = this.ResolveInvoiceReferenceFromChanges(registry);
        if (_fromChanges){
            return _fromChanges;
        }

        const _seriesPrefix = series.length > 0 ? series[0].toUpperCase() : '';
        const _invoicePrefix = invoice.length > 0 ? invoice[0].toUpperCase() : '';
        
        if (['C', 'F', 'R'].includes(_seriesPrefix) || ['C', 'F', 'R'].includes(_invoicePrefix)){
            return {
                series: series || null,
                invceno: invoice || null,
            };
        }

        return {
            series: null,
            invceno: null,
        };
    }

    private ResolveInvoiceReferenceFromChanges(registry: TicketDataRegistry): FiscalRefParts | null {
        const _changes = Array.isArray(registry.changes) ? registry.changes : [];

        for (let index = _changes.length - 1; index >= 0; index--){
            const _change = _changes[index];
            const _reason = String(_change?.reason || '').trim().toUpperCase();
            if (_reason !== 'I' && _reason !== 'D' && _reason !== 'P'){
                continue;
            }
            const _parts = this.FiscalRefFromChange(_change);
            if (_parts){
                return _parts;
            }
        }

        for (let index = _changes.length - 1; index >= 0; index--){
            const _change = _changes[index];
            const _series = String(_change?.series || '').trim();
            const _invoice = String(_change?.invoice || '').trim();
            if (_series.startsWith('F') || _series.startsWith('R')
                || _invoice.startsWith('F') || _invoice.startsWith('R')){
                const _parts = this.FiscalRefFromChange(_change);
                if (_parts){
                    return _parts;
                }
            }
        }

        return null;
    }

    private FiscalRefFromChange(change: { series?: unknown; invoice?: unknown } | null | undefined): FiscalRefParts | null {
        const _series = String(change?.series || '').trim();
        const _invoice = String(change?.invoice || '').trim();
        if (_series.length === 0 && _invoice.length === 0){
            return null;
        }

        return {
            series: _series || null,
            invceno: _invoice || null,
        };
    }

    /* === Fiscal helpers === */

    private ResolveFiscalQrFromRegistry(registry: TicketDataRegistry): FiscalQrBlock {
        const _system = String(registry.fiscal?.system || '').trim().toUpperCase();
        const _ticketBai = registry.fiscal?.qr?.ticketbai || null;
        const _verifactu = registry.fiscal?.qr?.verifactu || null;
        const _ticketBaiQr = String(_ticketBai?.qr || '').trim();
        const _ticketBaiId = String(_ticketBai?.id || '').trim();
        const _verifactuQr = String(_verifactu?.qr || '').trim();

        if (_system === 'TICKETBAI' && _ticketBaiQr){
            return {
                ticketbai_info: _ticketBaiId || null,
                ticketbai_qr_value: _ticketBaiQr,
                verifactu_qr_value: null,
            };
        }

        if (_system === 'VERIFACTU' && _verifactuQr){
            return {
                ticketbai_info: null,
                ticketbai_qr_value: null,
                verifactu_qr_value: _verifactuQr,
            };
        }

        return {
            ticketbai_info: null,
            ticketbai_qr_value: null,
            verifactu_qr_value: null,
        };
    }
}

/********************************/
/* UppTicketPrintComponent      */
/********************************/

@Component({
    selector: 'upp-ticket-print',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './upp-print.html',
    styleUrls: ['./upp-print.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UppTicketPrintComponent extends languageSupport implements OnChanges {
    @Input() ticket: TicketView | null = null;
    @Input() payload: string | null = null;
    @Input() display = true;
    @Input() format: TicketPrintFormat | string = 'thermal';

    private _ticketToPrint: TicketToPrint | null = null;
    private _payloadInfo: TicketPrintInfo | null = null;
    private _printFormat: TicketPrintFormat = 'thermal';
    private _ticketBaiQrValue: string | null = null;
    private _ticketBaiQrDataUrl: string | null = null;
    private _verifactuQrValue: string | null = null;
    private _verifactuQrDataUrl: string | null = null;

    private _place_subscription: Subscription | null = null;
    private _ticket_subscription: Subscription | null = null;

    get place(): PlaceView | null {
        return this.ticket?.place ?? null;
    }

    constructor(
        private lang: languageService,
        private data: dataService,
        private change: ChangeDetectorRef,
        private host: ElementRef<HTMLElement>,
    ) {
        super(lang, change);
    }

    private _printing = false;

    /**
     * `true` while a silent print dispatch is open (between `Printable`
     * and setting it back to `false`). Same "render only what's needed"
     * pattern as `upp-ticket-order.IsPrinting`: while merely watched
     * (`display=false`, not printing), the template skips building the
     * receipt markup so hundreds of watched tickets don't each hold a
     * rendered DOM tree.
     */
    get IsPrinting(): boolean {
        return this._printing;
    }

    set IsPrinting(value: boolean) {
        this._printing = value;
        this.change.markForCheck();
    }

    /**
     * Opens the print-render window: forces `Info`/QR to the current ticket
     * state and renders the markup the template otherwise skips while hidden.
     * Caller ({@link queueService}) must set `IsPrinting = false` once
     * `printService.printTicket` finished, to release the DOM again.
     */
    async Printable(): Promise<HTMLElement | null> {
        if (!this.ticket){
            return null;
        }

        this.IsPrinting = true;
        await this.Refresh();
        await this.ToPrint?.EmbedLogoForDevice();
        this.change.detectChanges();

        const _element = this.host.nativeElement;
        if (!_element){
            console.error('[TICKET PRINT] printable host missing after refresh');
            return null;
        }

        return _element;
    }

    ngOnChanges(changes: SimpleChanges = {}): void {
        this._printFormat = this.ResolvePrintFormat(this.format);

        const _ticket_changed = changes['ticket'] != null;
        const _display_changed = changes['display'] != null;
        const _payload_changed = changes['payload'] != null;

        if (_ticket_changed){
            this._ticketToPrint = null;
        }

        if (_payload_changed){
            this._payloadInfo = this.DecodePayload(this.payload);
        }

        if (_ticket_changed || _display_changed){
            this.RefreshBindings();
        }

        const _hasTrackedChanges = changes['ticket'] != null || changes['payload'] != null || changes['format'] != null;
        if (this.display === true && (_hasTrackedChanges || Object.keys(changes).length === 0)){
            void this.Refresh();
        }
    }

    get IsThermalLayout(): boolean {
        return this._printFormat === 'thermal';
    }

    get IsA5Layout(): boolean {
        return this._printFormat === 'A5';
    }

    @HostBinding('class.upp-ticket-print--thermal')
    get HostIsThermalLayout(): boolean {
        return this.IsThermalLayout;
    }

    @HostBinding('class.upp-ticket-print--a5')
    get HostIsA5Layout(): boolean {
        return this.IsA5Layout;
    }

    private ResolvePrintFormat(value: unknown): TicketPrintFormat {
        const _raw = String(value ?? '').trim().toLowerCase();
        if (_raw === 'a5'){
            return 'A5';
        }
        return 'thermal';
    }

    override OnDestroy(): void {
        this._ticketToPrint = null;
        this.ResetTicketSubscription();
        this.ResetPlaceSubscription();
        super.OnDestroy();
    }

    get ToPrint(): TicketToPrint | null {
        if (!this.ticket?.ticketdata){
            return null;
        }

        this.SyncPrintRegistry();

        if (!this._ticketToPrint){
            this._ticketToPrint = new TicketToPrint(this.ticket.ticketdata, this.ticket.object, this.place, this.data);
        }
        return this._ticketToPrint;
    }

    /**
     * Rebuilds registry from the live ticket before mapping when the snapshot
     * is missing, number-stale, or still DRAFT after payment landed on the
     * ticket (guest preview never goes through `TicketCommit.PrintTicket`).
     *
     * Stable unpaid DRAFT must not be rebuilt on every `Info` read: each
     * `TicketData.Refresh` marks `ToUpdate` and retriggers sync/OnRefresh,
     * which re-enters this getter and spins the UI at 100% CPU.
     */
    private SyncPrintRegistry(): void {
        const _ticket = this.ticket;
        const _ticketdata = _ticket?.ticketdata;
        if (!_ticket || !_ticketdata){
            return;
        }

        const _registry = _ticketdata.Registry;
        const _liveSeries = _ticket.series ?? null;
        const _liveInv = _ticket.invceno ?? null;
        const _regSeries = _registry?.ticketRef?.series ?? null;
        const _regInv = _registry?.ticketRef?.invceno ?? null;
        const _staleNumbers =
            (_liveSeries != null && _liveSeries !== _regSeries)
            || (_liveInv != null && _liveInv !== _regInv);
        const _missing = _registry == null;
        // Promote DRAFT → paid snapshot only when PaidFlag lands (not mere
        // pending payment=PAYLINE/PAYCASH request — that stays proforma).
        const _draftNeedsPaymentSync =
            _registry?.fiscal?.status === 'DRAFT' && _ticket.IsPaid;
        // CancelPayment / SQL clear can leave commercial.payments PAYLINE while
        // the live ticket is unpaid — rebuild so preview matches UnpaidTickets.
        const _stalePaymentSnapshot =
            !_ticket.IsPaid
            && ((_registry?.commercial?.payments?.length ?? 0) > 0
                || _registry?.fiscal?.status === 'READY');

        if (!_missing && !_staleNumbers && !_draftNeedsPaymentSync && !_stalePaymentSnapshot){
            return;
        }

        try {
            _ticketdata.Refresh(_ticket);
            this._ticketToPrint = null;
        }
        catch (error) { // Corrupt registry / incomplete live ticket: keep mapper null (RV-PRN-001).
            console.error('[TICKET PRINT] registry refresh failed', error);
        }
    }

    get Info(): TicketPrintInfo | null {
        // Silent queue hosts (display=false) must not map the receipt until
        // Printable opens the print window — otherwise every Watch() CD rebuilds
        // TicketToPrint / SyncPrintRegistry for tickets that are only listed.
        if (!this.display && !this.IsPrinting){
            return null;
        }
        if (this.ticket && this._payloadInfo){
            return null;
        }
        return this.ResolvePayloadInfo(this._payloadInfo) || this.ToPrint?.info() || null;
    }

    get TicketInfo(): TicketPrintInfo | null {
        return this.Info;
    }

    get BaiQR(): string | null {
        return this._ticketBaiQrDataUrl;
    }

    get VftQR(): string | null {
        return this._verifactuQrDataUrl;
    }

    /**
     * Emits RECEIPT activity event using this component payload.
     * The receipt structure remains owned by upp-ticket-print and transparent to consumers.
     */
    LogReceipt(info: TicketPrintInfo | null = null): boolean {
        const _info = info || this.Info;
        const _ticket = this.ticket;
        if (!_info || !_ticket?.place){
            return false;
        }

        const _payload: TicketPrintInfo = {
            ..._info,
            logo: null,
        };

        const _encoded = this.EncodePayload(_payload);
        if (_encoded == null){
            return false;
        }

        return _ticket.LogPrint(_encoded, Number(_payload.total || 0));
    }

    private RefreshBindings(): void {
        if (this.display === true){
            this.BindTicketRefresh();
            this.BindPlaceRefresh();
        }
        else {
            this.ResetTicketSubscription();
            this.ResetPlaceSubscription();
        }
    }

    private ResetTicketSubscription(): void {
        this._ticket_subscription?.unsubscribe();
        this._ticket_subscription = null;
    }

    private BindTicketRefresh(): void {
        this.ResetTicketSubscription();
        if (this.ticket?.OnRefresh){
            this._ticket_subscription = this.ticket.OnRefresh.subscribe(() => {
                void this.Refresh();
            });
        }
    }

    private ResetPlaceSubscription(): void {
        this._place_subscription?.unsubscribe();
        this._place_subscription = null;
    }

    private BindPlaceRefresh(): void {
        this.ResetPlaceSubscription();
        if (this.place?.OnRefresh){
            this._place_subscription = this.place.OnRefresh.subscribe(() => {
                void this.Refresh();
            });
        }
    }

    private async RefreshInfo(): Promise<TicketPrintInfo | null> {
        await this.SetLanguage(this.place?.lang || 'ES');

        if (this.ticket && this.ToPrint){
            return this.ToPrint.info(true);
        }

        return this.ResolvePayloadInfo(this._payloadInfo);
    }

    /**
     * Rebuilds `Info` and the fiscal QR images from the current ticket/payload.
     * Internal: reactive bindings call it on ticket/place changes, and
     * {@link Printable} calls it once, on demand, to build the receipt DOM
     * right before printing.
     */
    private async Refresh(): Promise<void> {
        if (this.ticket && this._payloadInfo){
            this._ticketBaiQrValue = null;
            this._verifactuQrValue = null;
            this._ticketBaiQrDataUrl = null;
            this._verifactuQrDataUrl = null;
            this.change.markForCheck();
            return;
        }

        const _info = await this.RefreshInfo();
        const _ticketBaiQr = _info?.ticketbai_qr_value || null;
        const _verifactuQr = _info?.verifactu_qr_value || null;

        // Rebuild each QR image only when its source value actually changes.
        if (_ticketBaiQr !== this._ticketBaiQrValue){
            this._ticketBaiQrValue = _ticketBaiQr;
            this._ticketBaiQrDataUrl = _ticketBaiQr ? await this.RefreshQR(_ticketBaiQr) : null;
        }
        if (_verifactuQr !== this._verifactuQrValue){
            this._verifactuQrValue = _verifactuQr;
            this._verifactuQrDataUrl = _verifactuQr ? await this.RefreshQR(_verifactuQr) : null;
        }

        this.change.markForCheck();
    }

    private ResolvePayloadInfo(payloadInfo: TicketPrintInfo | null): TicketPrintInfo | null {
        if (!payloadInfo){
            return null;
        }

        const _place = this.place;
        if (!_place){
            return payloadInfo;
        }

        const _showLogo = _place.GetConfigOption('optionPrintLogo', false);
        if (!_showLogo){
            return payloadInfo.logo ? { ...payloadInfo, logo: null } : payloadInfo;
        }

        if (payloadInfo.logo){
            return {
                ...payloadInfo,
                logo: TicketToPrint.ResolveLogoSrc(payloadInfo.logo),
            };
        }

        const _placeLogo = TicketToPrint.ResolveLogoSrc(_place.logo);
        if (_placeLogo == null){
            return payloadInfo;
        }

        return {
            ...payloadInfo,
            logo: _placeLogo,
        };
    }

    private DecodePayload(payload: string | null): TicketPrintInfo | null {
        const _raw = String(payload || '').trim();
        if (_raw === ''){
            return null;
        }

        try {
            const _json = GenericUtils.base64ToString(_raw);
            if (!_json){
                return null;
            }
            return JSON.parse(_json) as TicketPrintInfo;
        }
        catch {
            console.error('[TICKET PRINT] Invalid receipt payload:', _raw);
            return null;
        }
    }

    private EncodePayload(info: TicketPrintInfo): string | null {
        try {
            return GenericUtils.stringToBase64(JSON.stringify(info));
        }
        catch {
            console.error('[TICKET PRINT] Failed to encode receipt payload');
            return null;
        }
    }

    private async RefreshQR(url: string): Promise<string | null> {
        const _url = String(url || '').trim();
        if (_url.length === 0){
            return null;
        }

        try {
            return await toDataURL(_url, {
                errorCorrectionLevel: 'H',
                margin: 0,
                width: 512,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF',
                },
            });
        }
        catch (error) {
            console.error('[TICKET PRINT] QR build failed', error);
            return null;
        }
    }
}
